/**
 * Mock identity for `VITE_USE_MOCK_DATA=true`.
 *
 * Login accepts any email, and the address that was typed becomes the
 * signed-in user so a prototype shows a realistic name in the navbar. The one
 * scripted failure is the password `"wrong"`, which returns `null` — that is
 * how the real backend reports bad credentials (HTTP 200 with an empty `data`),
 * so mock mode can exercise the login guard without a server.
 */

import type {
  AuthSessionResponse,
  LoginRequest,
  UserResponse,
} from "@shared/contracts";
import { mockUsers } from "./users.mock";

/** The password that makes `mockLogin` report a credential failure. */
export const MOCK_REJECTED_PASSWORD = "wrong";

export const mockCurrentUser: UserResponse = { ...mockUsers[0] };

export function mockLogin(body: unknown): AuthSessionResponse | null {
  const request = (body ?? {}) as Partial<LoginRequest>;

  if (request.password === MOCK_REJECTED_PASSWORD) {
    return null;
  }

  const email =
    typeof request.email === "string" && request.email.trim()
      ? request.email.trim()
      : mockCurrentUser.email;

  mockCurrentUser.email = email;

  return mockSession({ ...mockCurrentUser });
}

export function mockRegister(body: unknown): AuthSessionResponse {
  const request = (body ?? {}) as Partial<{
    first_name: string;
    last_name: string;
    email: string;
  }>;

  Object.assign(mockCurrentUser, {
    first_name: request.first_name ?? mockCurrentUser.first_name,
    last_name: request.last_name ?? mockCurrentUser.last_name,
    email: request.email ?? mockCurrentUser.email,
  });

  return mockSession({ ...mockCurrentUser });
}

/** Registration, login, refresh and profile-update all resolve this shape. */
export function mockSession(user: UserResponse): AuthSessionResponse {
  const stamp = Date.now();
  return {
    user,
    token: `mock-token-${stamp}`,
    refresh_token: `mock-refresh-token-${stamp}`,
  };
}
